import { RELEASE_APRIL_TAX_SERVICES } from '#client/constants/experimentsFlags';

import { TaxPartnerExclusivityGate } from '../components/gates/TaxPartnerExclusivityGate';
import { getBuyButtonConfig } from '../utils/commonEntitlements';
import {
  BUSINESS_FINANCE_TAX_ACCOUNT_PARTNERS,
  BUSINESS_ENTITY_TAX_CLASSIFICATION,
  TAX_CLASSIFICATION_TITLE_MAP
} from '../utils/taxUtils';

const DEFAULT_BULLET_POINTS = [
  { title: 'productPage.quarterlyTaxPlanning.buyBox.bulletPoint1' },
  { title: 'productPage.quarterlyTaxPlanning.buyBox.bulletPoint2' },
  { title: 'productPage.quarterlyTaxPlanning.buyBox.bulletPoint3' }
];

const BULLET_POINTS_BY_TAX_CLASSIFICATION_MAP = Object.freeze({
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.SINGLE_MEMBER_LLC]: [
    { title: 'productPage.quarterlyTaxPlanning.buyBox.singleMemberLlc.bulletPoint1' },
    { title: 'productPage.quarterlyTaxPlanning.buyBox.singleMemberLlc.bulletPoint2' },
    { title: 'productPage.quarterlyTaxPlanning.buyBox.singleMemberLlc.bulletPoint3' }
  ],
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.S_CORPORATION]: DEFAULT_BULLET_POINTS,
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.C_CORPORATION]: DEFAULT_BULLET_POINTS,
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.PARTNERSHIP]: DEFAULT_BULLET_POINTS
});

const getBulletPointsByTaxClassification = ({ taxClassification }) => (
  BULLET_POINTS_BY_TAX_CLASSIFICATION_MAP[taxClassification] || []
);

const DEFAULT_HOW_IT_WORKS_ITEMS = [
  {
    title: 'productPage.quarterlyTaxPlanning.howItWorks.step1',
    description: 'productPage.quarterlyTaxPlanning.howItWorks.shortDescription1'
  },
  {
    title: 'productPage.quarterlyTaxPlanning.howItWorks.step2',
    description: 'productPage.quarterlyTaxPlanning.howItWorks.shortDescription2'
  },
  {
    title: 'productPage.quarterlyTaxPlanning.howItWorks.step3',
    description: 'productPage.quarterlyTaxPlanning.howItWorks.shortDescription3'
  }
];

const HOW_IT_WORKS_ITEMS_BY_TAX_CLASSIFICATION_MAP = Object.freeze({
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.SINGLE_MEMBER_LLC]: [
    {
      title: 'productPage.quarterlyTaxPlanning.howItWorks.singleMemberLlc.step1',
      description: 'productPage.quarterlyTaxPlanning.howItWorks.singleMemberLlc.shortDescription1'
    },
    {
      title: 'productPage.quarterlyTaxPlanning.howItWorks.singleMemberLlc.step2',
      description: 'productPage.quarterlyTaxPlanning.howItWorks.singleMemberLlc.shortDescription2'
    },
    {
      title: 'productPage.quarterlyTaxPlanning.howItWorks.singleMemberLlc.step3',
      description: 'productPage.quarterlyTaxPlanning.howItWorks.singleMemberLlc.shortDescription3'
    }
  ],
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.S_CORPORATION]: DEFAULT_HOW_IT_WORKS_ITEMS,
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.C_CORPORATION]: DEFAULT_HOW_IT_WORKS_ITEMS,
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.PARTNERSHIP]: DEFAULT_HOW_IT_WORKS_ITEMS
});

const getHowItWorksItemsByTaxClassification = ({ taxClassification }) => (
  HOW_IT_WORKS_ITEMS_BY_TAX_CLASSIFICATION_MAP[taxClassification] || []
);

const BENEFITS_BY_TAX_CLASSIFICATION_MAP = Object.freeze({
  [TAX_CLASSIFICATION_TITLE_MAP[BUSINESS_ENTITY_TAX_CLASSIFICATION.S_CORPORATION]]: [
    {
      icon: '/s2/material-icons/factcheckrounded.svg',
      title: 'productPage.quarterlyTaxPlanning.whatYouGet.sCorporation.item1.title',
      description: 'productPage.quarterlyTaxPlanning.whatYouGet.sCorporation.item1.desc'
    },
    {
      icon: '/s2/material-icons/supportagentrounded.svg',
      title: 'productPage.quarterlyTaxPlanning.whatYouGet.sCorporation.item2.title',
      description: 'productPage.quarterlyTaxPlanning.whatYouGet.sCorporation.item2.desc'
    },
    {
      icon: '/s2/material-icons/tableviewrounded.svg',
      title: 'productPage.quarterlyTaxPlanning.whatYouGet.sCorporation.item3.title',
      description: 'productPage.quarterlyTaxPlanning.whatYouGet.sCorporation.item3.desc'
    }
  ],
  [TAX_CLASSIFICATION_TITLE_MAP[BUSINESS_ENTITY_TAX_CLASSIFICATION.PARTNERSHIP]]: [
    {
      icon: '/s2/material-icons/factcheckrounded.svg',
      iconColor: 'info.dark',
      iconBgColor: 'background.cyan.light',
      title: 'productPage.quarterlyTaxPlanning.whatYouGet.partnership.item1.title',
      description: 'productPage.quarterlyTaxPlanning.whatYouGet.partnership.item1.desc'
    },
    {
      icon: '/s2/material-icons/supportagentrounded.svg',
      iconColor: 'info.dark',
      iconBgColor: 'background.cyan.light',
      title: 'productPage.quarterlyTaxPlanning.whatYouGet.partnership.item2.title',
      description: 'productPage.quarterlyTaxPlanning.whatYouGet.partnership.item2.desc'
    },
    {
      icon: '/s2/material-icons/tableviewrounded.svg',
      iconColor: 'info.dark',
      iconBgColor: 'background.cyan.light',
      title: 'productPage.quarterlyTaxPlanning.whatYouGet.partnership.item3.title',
      description: 'productPage.quarterlyTaxPlanning.whatYouGet.partnership.item3.desc'
    }
  ],
  [TAX_CLASSIFICATION_TITLE_MAP[BUSINESS_ENTITY_TAX_CLASSIFICATION.C_CORPORATION]]: [
    {
      icon: '/s2/material-icons/factcheckrounded.svg',
      iconColor: 'tertiary.dark',
      iconBgColor: 'background.purple.light',
      title: 'productPage.quarterlyTaxPlanning.whatYouGet.cCorporation.item1.title',
      description: 'productPage.quarterlyTaxPlanning.whatYouGet.cCorporation.item1.desc'
    },
    {
      icon: '/s2/material-icons/supportagentrounded.svg',
      iconColor: 'tertiary.dark',
      iconBgColor: 'background.purple.light',
      title: 'productPage.quarterlyTaxPlanning.whatYouGet.cCorporation.item2.title',
      description: 'productPage.quarterlyTaxPlanning.whatYouGet.cCorporation.item2.desc'
    },
    {
      icon: '/s2/material-icons/tableviewrounded.svg',
      iconColor: 'tertiary.dark',
      iconBgColor: 'background.purple.light',
      title: 'productPage.quarterlyTaxPlanning.whatYouGet.cCorporation.item3.title',
      description: 'productPage.quarterlyTaxPlanning.whatYouGet.cCorporation.item3.desc'
    }
  ]
});

const BENEFIT_ITEMS_BY_TAX_CLASSIFICATION_MAP = Object.freeze({
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.SINGLE_MEMBER_LLC]: [
    {
      icon: '/s2/material-icons/percentrounded.svg',
      title: 'productPage.quarterlyTaxPlanning.whatYouGet.item1.title',
      description: 'productPage.quarterlyTaxPlanning.whatYouGet.item1.desc'
    },
    {
      icon: '/s2/material-icons/insertinvitationrounded.svg',
      title: 'productPage.quarterlyTaxPlanning.whatYouGet.item2.title',
      description: 'productPage.quarterlyTaxPlanning.whatYouGet.item2.desc'
    },
    {
      icon: '/s2/material-icons/tableviewrounded.svg',
      title: 'productPage.quarterlyTaxPlanning.whatYouGet.item3.title',
      description: 'productPage.quarterlyTaxPlanning.whatYouGet.item3.desc'
    }
  ],
  // Benefits with tabbed categories
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.S_CORPORATION]: BENEFITS_BY_TAX_CLASSIFICATION_MAP,
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.C_CORPORATION]: BENEFITS_BY_TAX_CLASSIFICATION_MAP,
  [BUSINESS_ENTITY_TAX_CLASSIFICATION.PARTNERSHIP]: BENEFITS_BY_TAX_CLASSIFICATION_MAP
});

const getBenefitByTaxClassification = ({ taxClassification }) => ({
  title: taxClassification === BUSINESS_ENTITY_TAX_CLASSIFICATION.SINGLE_MEMBER_LLC ? 'productPage.quarterlyTaxPlanning.whatYouGet.singleMemberLlc.title' : 'productPage.quarterlyTaxPlanning.whatYouGet.title',
  items: BENEFIT_ITEMS_BY_TAX_CLASSIFICATION_MAP[taxClassification] || [],
  defaultCategory: TAX_CLASSIFICATION_TITLE_MAP[taxClassification]
});

export const QUARTERLY_TAX_PLANNING_CONFIG = {
  isTaxClassificationDependent: true,
  hero: {
    buyBox: [{
      bulletPoints: getBulletPointsByTaxClassification,
      productName: 'productPage.quarterlyTaxPlanning.buyBox.productName',
      interactService: {
        ctaType: 'internalCheckout',
        service: 'zb_quarterly_tax_planning'
      }
    }],
    headerPicture: {
      src: '/s2/raster/happy-woman-talking-on-the-phone.png',
      alt: 'productPage.quarterlyTaxPlanning.heroPicture.altText',
      objectPosition: 'center'
    },
    headline: 'productPage.quarterlyTaxPlanning.page.headline',
    subheader: 'productPage.quarterlyTaxPlanning.page.subheader',
    partnerLogo: { src: '/s2/vector/april-logo-purple.svg', alt: 'April Logo' },
    partnerReviewBlanks: [
      { text: 'productPage.quarterlyTaxPlanning.page.reviewText' },
      { src: '/illustration/trustpilot-4.5-star.svg', showOnMobile: true, alt: '' },
      { src: '/illustration/trustpilot-logo.svg', alt: '' }
    ]
  },
  benefit: getBenefitByTaxClassification,
  howItWorks: {
    title: 'productPage.quarterlyTaxPlanning.howItWorks.title',
    items: getHowItWorksItemsByTaxClassification,
    image: '/s2/raster/couple-owners-restaurant.png',
    alt: 'productPage.quarterlyTaxPlanning.howItWorks.altText',
    hasNumber: true,
    hasDivider: false,
    hasMobileImage: true
  },
  whatsIncluded: {
    title: 'productPage.quarterlyTaxPlanning.whatsIncluded.title',
    items: [
      {
        title: 'productPage.quarterlyTaxPlanning.whatsIncluded.item1',
        description: 'productPage.quarterlyTaxPlanning.whatsIncluded.item1ShortDescription'
      },
      {
        title: 'productPage.quarterlyTaxPlanning.whatsIncluded.item2',
        description: 'productPage.quarterlyTaxPlanning.whatsIncluded.item2ShortDescription'
      }
    ],
    image: '/s2/raster/tax-planning-reminders.png',
    alt: 'productPage.quarterlyTaxPlanning.whatsIncluded.altText',
    hasIcon: true,
    hasNumber: false,
    hasDivider: false,
    hasMobileImage: true
  },
  relatedProducts: {
    title: 'productPage.quarterlyTaxPlanning.relatedProducts.title',
    items: [
      {
        title: 'productPage.quarterlyTaxPlanning.relatedProducts.taxFiling.title',
        description: 'productPage.quarterlyTaxPlanning.relatedProducts.taxFiling.description',
        productSlug: 'tax-filing',
        entitlement: 'zb_tax_full_service_biz_per_llc'
      },
      {
        title: 'dashboard.products.money_pro.title',
        description: 'dashboard.products.money_pro.description',
        productSlug: 'money-pro',
        entitlement: 'money_pro'
      }
    ]
  },
  assistance: {
    title: 'productPage.quarterlyTaxPlanning.needAssistance.title',
    getInTouch: 'productPage.quarterlyTaxPlanning.needAssistance.getInTouch',
    actions: 'productPage.quarterlyTaxPlanning.needAssistance.actions',
    icon: '/s2/material-icons/phonerounded.svg',
    additionalAssistance: [
      {
        icon: '/s2/material-icons/insertinvitationrounded.svg',
        title: 'productPage.quarterlyTaxPlanning.needAssistance.expert.title',
        actions: 'productPage.quarterlyTaxPlanning.needAssistance.expert.cta',
        href: 'https://calendly.com/d/cvh6-zw4-zkp/tax-consultation'
      }
    ]
  },
  // Configure product gates (feature flags, component with custom logic, etc.) useful for conditional redirects
  productGates: {
    flagGate: {
      flagKey: RELEASE_APRIL_TAX_SERVICES,
      redirectPath: '/products/tax-advice'
    },
    componentGates: [{
      Component: TaxPartnerExclusivityGate,
      props: {
        partnerName: BUSINESS_FINANCE_TAX_ACCOUNT_PARTNERS.APRIL,
        redirectPath: '/products/tax-advice'
      }
    }]
  },
  getBuyButtonConfig: (entitlements, service) => getBuyButtonConfig({
    entitlements,
    service,
    ctaText: 'productPage.quarterlyTaxPlanning.buyBox.buttonLabel'
  })
};
